import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaCalendarAlt, FaArrowLeft } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import { fetchClassOptions } from "../../../../redux/Masterdata";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const ExamScheduleView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const scheduleData = location.state?.scheduleData || null;
  const { translations } = useSelector((state) => state.language);
  const { classOptions } = useSelector((state) => state.masterData);

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const class_id = clientData?.class_id || "";
  const section_id = clientData?.student_section || "";
  const roleId = Number(user?.role?.id);
  const userId = user?.id || "";

  const isExamTimeTable = location.pathname.includes("examTimeTable");

  const [loading, setLoading] = useState(false);
  const [viewData, setViewData] = useState([]);

  const [selectedExam, setSelectedExam] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [examNameOptions, setExamNameOptions] = useState([]);
  const [deleting, setDeleting] = useState(false);
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });

  const classDropdownOptions = [
    { value: "", label: translations.labels?.all || "All" },
    ...classOptions,
  ];

  // Helper: Map API response array into viewData array
  const mapResponseToViewData = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return [];

    return dataArray.map((section) => ({
      ...section,
      schedule_details: (section.schedule_details || []).map((sub, index) => ({
        sno: index + 1,
        subject_name: sub.subject_name || "-",
        mode_of_exam: sub.mode_of_exam || "-",
        date_of_exam: sub.date || "-",
        pass_mark: sub.pass_mark || 0,
        max_mark: sub.max_marks || 0,
        from_time: sub.from_time || "-",
        to_time: sub.end_time || "-",
        duration: sub.duration || "-",
      })),
    }));
  };

  const fetchExamNames = useCallback(async () => {
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };
      const response = await AxiosInstance.post(
        "/exam/examConfig/list",
        payload,
      );
      if (response?.data?.status) {
        const examData = response.data.data || [];
        const options = [
          {
            value: "",
            label: translations.labels?.allExams || "All Exams",
          },
          ...examData.map((item) => ({
            value: item.id,
            label: item.exam_type || item.exam_name,
          })),
        ];
        setExamNameOptions(options);
      }
    } catch (error) {
      console.error("Error fetching exam names:", error);
    }
  }, [school_id, academic_year_id, translations]);

  const fetchExamTimeTableData = useCallback(async () => {
    if (!selectedExam) return;

    try {
      setLoading(true);

      let response;

      if (roleId === 6) {
        const payload = {
          id: userId,
          config_id: Number(selectedExam),
          class_id: selectedClass ? Number(selectedClass) : "",
        };
        response = await AxiosInstance.post(
          "/exam/examSchedule/Parentview",
          payload,
        );
      } else {
        const payload = {
          config_id: Number(selectedExam),
          class_id:
            roleId === 4
              ? class_id || ""
              : selectedClass
                ? Number(selectedClass)
                : "",
          section_id: section_id,
        };
        response = await AxiosInstance.post("/exam/examSchedule/view", payload);
      }

      if (response?.data?.status) {
        const dataArray = response.data.data || [];
        setViewData(mapResponseToViewData(dataArray));
      } else {
        setViewData([]);
      }
    } catch (error) {
      console.error("Error fetching exam timetable data:", error);
      alert(
        translations.labels?.fetchFailed ||
          "Failed to fetch details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedExam, selectedClass, roleId, userId, translations]);

  const fetchViewData = useCallback(async () => {
    if (!scheduleData?.id) return;

    try {
      setLoading(true);

      const payload = {
        config_id: scheduleData.config_id,
        class_id: scheduleData.class_id,
        section_id: scheduleData.section_id,
      };

      const response = await AxiosInstance.post(
        "/exam/examSchedule/view",
        payload,
      );

      if (response?.data?.status) {
        const dataArray = response.data.data || [];
        setViewData(mapResponseToViewData(dataArray));
        // setModalState({
        //   open: true,
        //   type: "success",
        //   message: response.data?.message,
        //   redirectPath: "",
        // });
      } else {
        // setModalState({
        //   open: true,
        //   type: "error",
        //   message: response.data?.message,
        //   redirectPath: "",
        // });

        setViewData([]);
      }
    } catch (error) {
      console.error("Error fetching view data:", error);
      // setModalState({
      //   open: true,
      //   type: "error",
      //   message:
      //     translations.labels?.fetchFailed ||
      //     "Failed to fetch details. Please try again.",
      //   redirectPath: "",
      // });
    } finally {
      setLoading(false);
    }
  }, [scheduleData, translations]);

  // DELETE handler
  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      translations.labels?.confirmDelete ||
        "Are you sure you want to delete this exam schedule?",
    );
    if (!confirmDelete) return;

    try {
      setDeleting(true);
      const response = await AxiosInstance.delete("/exam/examSchedule/delete", {
        data: {
          class_id: viewData[0].class_id,
          section_id: viewData[0].section_id,
          school_id: viewData[0].school_id,
          config_id: viewData[0].config_id,
          deleted_by: parseInt(userId),
        },
      });

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            translations.labels?.deleteSuccess ||
            "Exam schedule deleted successfully",
          redirectPath: "/user/examSchedule",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            translations.labels?.deleteFailed ||
            "Failed to delete exam schedule",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error deleting exam schedule:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.deleteFailed || "Error deleting exam schedule",
      });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (isExamTimeTable) {
      fetchExamNames();
      dispatch(
        fetchClassOptions({
          id: parseInt(school_id),
          academic_year: academic_year,
        }),
      );
    }
  }, [isExamTimeTable, fetchExamNames]);

  useEffect(() => {
    if (isExamTimeTable && selectedExam) {
      fetchExamTimeTableData();
    }
  }, [isExamTimeTable, selectedExam, selectedClass, fetchExamTimeTableData]);

  useEffect(() => {
    if (!isExamTimeTable) {
      fetchViewData();
    }
  }, [isExamTimeTable, fetchViewData]);

  const handleExamChange = (e) => {
    const value = e?.target?.value ?? e?.value ?? null;

    setSelectedExam(value);
    setSelectedClass(null);
    setViewData([]);
  };

  const handleClassChange = (e) => {
    const value = e?.target?.value ?? e?.value ?? null;

    setSelectedClass(value);
    setViewData([]);
  };

  const formatTimeDisplay = (time) => {
    if (!time || time === "-") return "-";
    try {
      const parsed = dayjs(`2000-01-01T${time}`);
      if (parsed.isValid()) {
        return parsed.format("hh:mm A");
      }
      return time;
    } catch {
      return time;
    }
  };

  const formatDateDisplay = (date) => {
    if (!date || date === "-") return "-";
    try {
      const parsed = dayjs(date);
      if (parsed.isValid()) {
        return parsed.format("DD-MMM-YYYY");
      }
      return date;
    } catch {
      return date;
    }
  };

  const formatDuration = (duration) => {
    if (!duration || duration === "-") return "-";
    const hours = Math.floor(parseFloat(duration));
    const minutes = Math.round((parseFloat(duration) - hours) * 60);
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return "-";
  };

  const handleBack = () => {
    if (isExamTimeTable) {
      navigate(-1);
    } else {
      navigate("/user/examSchedule");
    }
  };

  const handleEdit = () => {
    navigate(`/user/examScheduleCreate`, {
      state: {
        scheduleData: scheduleData,
      },
    });
  };

  // Reusable table renderer for a single section
  const renderScheduleTable = (section, sectionIndex) => (
    <div key={sectionIndex} style={{ marginBottom: "24px" }}>
      {/* Section Info Row */}
      {isExamTimeTable && (
        <div className="row" style={{ marginBottom: "12px" }}>
          <div className="col-lg-3 px-2 col-md-4 col-sm-6">
            <CustomTextInput
              label={translations.labels?.class || "Class"}
              name={`class-${sectionIndex}`}
              type="text"
              value={section.class_name || "-"}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-4 col-sm-6">
            <CustomTextInput
              label={translations.labels?.section || "Section"}
              name={`section-${sectionIndex}`}
              type="text"
              value={section.section_id || "-"}
              disabled
              readOnly
            />
          </div>
        </div>
      )}

      {/* Schedule Table */}
      <div className="table-responsive">
        <table
          className="table table-bordered mb-0"
          style={{
            tableLayout: "fixed",
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#f5f5f5",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              <th
                style={{
                  width: "6%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.serialNo || "S.No"}
              </th>
              <th
                style={{
                  width: "16%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.subject || "Subject"}
              </th>
              <th
                style={{
                  width: "12%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.modeOfExam || "Mode of Exam"}
              </th>
              <th
                style={{
                  width: "13%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.dateOfExam || "Date of Exam"}
              </th>
              <th
                style={{
                  width: "10%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.passMark || "Pass Mark"}
              </th>
              <th
                style={{
                  width: "10%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.maxMark || "Max Mark"}
              </th>
              <th
                style={{
                  width: "11%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.startTime || "Start Time"}
              </th>
              <th
                style={{
                  width: "11%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.endTime || "End Time"}
              </th>
              <th
                style={{
                  width: "11%",
                  textAlign: "center",
                  padding: "12px 8px",
                }}
              >
                {translations.labels?.duration || "Duration"}
              </th>
            </tr>
          </thead>
          <tbody>
            {section.schedule_details?.length > 0 ? (
              section.schedule_details.map((subject) => (
                <tr
                  key={`${sectionIndex}-${subject.sno}`}
                  style={{
                    fontSize: "14px",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {subject.sno}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {subject.subject_name}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {subject.mode_of_exam === "theory"
                      ? translations.labels?.theory || "Theory"
                      : subject.mode_of_exam === "practical"
                        ? translations.labels?.practical || "Practical"
                        : subject.mode_of_exam}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {formatDateDisplay(subject.date_of_exam)}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {subject.pass_mark}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {subject.max_mark}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {formatTimeDisplay(subject.from_time)}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {formatTimeDisplay(subject.to_time)}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 8px" }}>
                    {formatDuration(subject.duration)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#999",
                  }}
                >
                  {translations.labels?.noRecordsToDisplay ||
                    "No records to display"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {isExamTimeTable
              ? translations.labels?.examTimeTable || "Exam Time Table"
              : translations.labels?.examSchedule || "Exam Schedule"}
          </h1>
        </div>
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          {translations.labels?.loading || "Loading..."}
        </div>
      </div>
    );
  }

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {isExamTimeTable
              ? translations.labels?.examTimeTable || "Exam Time Table"
              : translations.labels?.examSchedule || "Exam Schedule"}
          </h1>
        </div>

        {/* Back & Edit Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginBottom: "16px",
          }}
        >
          {!isExamTimeTable && (
            <CustomButton
              type="button"
              variant="contained"
              className={styles.addButton}
              fullWidth={false}
              onClick={handleEdit}
            >
              {translations.labels?.edit || "EDIT"}
            </CustomButton>
          )}
          {!isExamTimeTable && (
            <CustomButton
              type="button"
              variant="contained"
              className={styles.deleteButton || styles.cancelButton}
              fullWidth={false}
              loading={deleting}
              loadingText={translations.labels?.deleting || "Deleting..."}
              onClick={handleDelete}
            >
              {translations.labels?.deleteButton || "DELETE"}
            </CustomButton>
          )}
          <CustomButton
            type="button"
            variant="contained"
            className={styles.cancelButton || styles.addButton}
            fullWidth={false}
            onClick={handleBack}
          >
            {translations.labels?.back || "BACK"}{" "}
            <FaArrowLeft style={{ marginLeft: "6px" }} />
          </CustomButton>
        </div>

        {/* Top Info Row */}
        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.academicYear || "Academic Year"}
              name="academicYear"
              type="text"
              value={academic_year}
              disabled
              readOnly
            />
          </div>

          {isExamTimeTable && (
            <>
              <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label={translations.labels?.examName || "Exam Name"}
                  name="examName"
                  value={selectedExam}
                  onChange={handleExamChange}
                  options={examNameOptions}
                  placeholder={translations.labels?.allExams || "All Exams"}
                />
              </div>
              {roleId !== 6 && roleId !== 4 && (
                <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                  <CustomDropdown
                    label={translations.labels?.class || "Class"}
                    name="class"
                    value={selectedClass}
                    onChange={handleClassChange}
                    options={classDropdownOptions}
                    placeholder={translations.labels?.all || "All"}
                  />
                </div>
              )}
            </>
          )}

          {!isExamTimeTable && (
            <>
              <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={translations.labels?.examName || "Exam Name"}
                  name="examName"
                  type="text"
                  value={scheduleData?.exam_type || "-"}
                  disabled
                  readOnly
                />
              </div>

              <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={translations.labels?.class || "Class"}
                  name="class"
                  type="text"
                  value={viewData.length > 0 ? viewData[0].class_name : "-"}
                  disabled
                  readOnly
                />
              </div>

              <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={translations.labels?.section || "Section"}
                  name="section"
                  type="text"
                  value={viewData.length > 0 ? viewData[0].section_id : "-"}
                  disabled
                  readOnly
                />
              </div>
            </>
          )}
        </div>

        {/* Table Title */}
        <h3
          style={{
            textAlign: "center",
            margin: "20px 0 16px",
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          {isExamTimeTable
            ? translations.labels?.examTimeTableList || "Exam Time Table List"
            : translations.labels?.examScheduleList || "Exam Schedule List"}
        </h3>

        {/* Map entire table per section */}
        {viewData.length > 0 ? (
          viewData.map((section, index) => renderScheduleTable(section, index))
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#999",
              fontSize: "14px",
            }}
          >
            {isExamTimeTable && !selectedExam
              ? translations.labels?.selectExamAndClass ||
                "Please select Exam Name to view the timetable"
              : translations.labels?.noRecordsToDisplay ||
                "No records to display"}
          </div>
        )}
      </div>
    </>
  );
};

export default ExamScheduleView;
